import * as React from 'react';
import * as ReactDOM from 'react-dom';
import { connect } from 'react-redux';
import { State as AppStateType, resolveSyncIfLoadedOrThrow } from '@polarnopyret/scope';
import editorAttributesFor, { safetyCheckAttribute, safetyCheckClassName } from './editor-attributes-for';
import { format } from '@polarnopyret/scope';

export type ComponentType = string | React.ComponentType<any>;

export const supportsComponentPropName = 'supportsComponentProp';

type SupportsComponent = React.ComponentType<any>;

export function supportsComponentProp<TComponent extends SupportsComponent>(component: TComponent): TComponent {
  (component as any)[supportsComponentPropName] = true;
  return component;
}

type RequiredProps = {
  for: Scope.Property<any>;
  className?: string;
  component?: ComponentType;
  propertyComponentProps?: any;
  args?: { [key: string]: string | number };
};

type ConnectedState = {
  currentTheme: string;
};

type Props = RequiredProps & ConnectedState;

type State = {
  property?: Scope.Property<any>;
};

function htmlDecode(value: string) {
  const div = document.createElement('div');
  div.innerHTML = value;
  return div.textContent;
}

class EpiProperty extends React.PureComponent<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      property: props.for,
    };
  }
  componentDidMount() {
    if (!window.IS_IN_TEST_MODE) {
      if (process.env.NODE_ENV !== 'production') {
        const node = ReactDOM.findDOMNode(this) as Element;
        if (node && (!node.hasAttribute(safetyCheckAttribute) || !node.classList.contains(safetyCheckClassName))) {
          console.error('Editor attributes was not rendered on node', node);
          console.error(
            'When using a custom component to EpiProperty such as\n<EpiProperty for={property} component={innerProps => <div {...innerProps} />} />\n' +
              'you must always spread `innerProps` last on that element.\n\n' +
              'The following is not correct:\n' +
              '<EpiProperty for={property} component={innerProps => <div data-extra-attr="true">{innerProps.children}</div>} />\n' +
              '<EpiProperty for={property} component={innerProps => <div {...innerProps} data-extra-attr="true" className="my-class" />} />\n\n' +
              'Instead, write it like this:\n' +
              '<EpiProperty for={property} component={innerProps => <div {...innerProps} className={classnames("my-class", innerProps.className)} />} />\n' +
              '<EpiProperty for={property} component={innerProps => <div data-extra-attr="true" {...innerProps} />',
          );

          const style =
            'position:fixed;top:0;left:0;bottom:0;right:0;background-color:red;padding:50px;z-index:99999;font-size:100px;color:white';
          node.innerHTML = '<div style="' + style + '">ERROR! Check console.</div>';
          throw new Error('Editor attributes was not rendered on node');
        }
      }

      if (window.IS_IN_EDIT_MODE) {
        const node = ReactDOM.findDOMNode(this) as Element;
        const component = this;

        Object.defineProperty(node, 'innerHTML', {
          get() {
            let html = '';
            // tslint:disable-next-line prefer-for-of
            for (let i = 0; i < node.childNodes.length; i++) {
              const child = node.childNodes[i];
              if (child instanceof Element) {
                html += child.innerHTML;
              }
            }
            return html;
          },
          set(value: string) {
            // Because Epi wants to replace empty content with a space, probably to make it take up some space
            if (value === '&nbsp;') {
              return;
            }
            let content: any;
            const jsonPragma = '$property-for-json:';
            if (value && value.indexOf(jsonPragma) === 0) {
              content = JSON.parse(value.substring(jsonPragma.length));
            } else {
              content = htmlDecode(value);
            }

            // If content is null, it means that the content was a complex type and that it has now been
            // removed. But we still want to use the same component to render it since otherwise React
            // will throw away the DOM node
            if (content === null) {
              content = {
                componentName: (component.props.for as any).$c.componentName,
              };
            }
            const property = Object.assign({}, component.state.property, { $c: content });
            component.setState({ property });
          },
          configurable: true,
        });
      }
    }
  }
  UNSAFE_componentWillReceiveProps(newProps: Props) {
    this.setState({ property: newProps.for });
  }
  base = (innerProps: any) => {
    const { className: outerClassName, component: Component = 'div' } = this.props;
    const { className: innerClassName, ...restInnerProps } = innerProps;
    const { property } = this.state;
    const name = property && (property as any)._name;

    let mergedClassName = '';
    if (outerClassName) {
      mergedClassName += ` ${outerClassName}`;
    }
    if (innerClassName) {
      mergedClassName += ` ${innerClassName}`;
    }

    const editorAttributes = editorAttributesFor(name, mergedClassName.slice(1));

    return <Component {...restInnerProps} {...editorAttributes} />;
  };
  render() {
    const { property } = this.state;
    let content = property ? (property as any).$c : null;

    if (!window.IS_IN_EDIT_MODE && !content) {
      return null;
    }

    const name = property && (property as any)._name;
    content = typeof content === 'string' && this.props.args ? format(content, this.props.args) : content;

    let PropertyComponent: React.ComponentType<any> | null = null;
    if (content && content.componentName) {
      PropertyComponent = resolveSyncIfLoadedOrThrow(content.componentName, this.props.currentTheme);
    }

    if (PropertyComponent) {
      if (typeof content === 'string') {
        content = { content };
      }

      if ((PropertyComponent as any)[supportsComponentPropName]) {
        return (
          <PropertyComponent
            component={this.base}
            propertyName={name}
            {...this.props.propertyComponentProps}
            {...content}
          />
        );
      }

      return (
        <this.base>
          <PropertyComponent {...this.props.propertyComponentProps} {...content} />
        </this.base>
      );
    }

    return <this.base>{content || ''}</this.base>;
  }
}

export default connect(
  (state: AppStateType): ConnectedState => ({
    currentTheme: state.currentTheme,
  }),
)(EpiProperty);
